import { Button } from '@sureco/design-system'
import { Alert, Modal, Spin, Table, Typography } from 'antd'
import Papa from 'papaparse'
import React, { useEffect, useMemo, useState } from 'react'

const { Text } = Typography

export type PreviewableCensusFile = {
  id: string
  fileName: string
  isSubmitted?: boolean
  isError?: boolean
}

type PreviewRow = {
  key: string
  _rowNum: number
  [key: string]: string | number
}

type GenericProps<T extends PreviewableCensusFile> = {
  open: boolean
  file: T | null
  getDownloadUrl: (id: string) => Promise<{ url: string }>
  censusVersion?: number
  onFileSubmit?: (file: T, censusVersion: number) => void
  isSubmitting?: boolean
  onCancel: () => void
}

const getColumnWidth = (header: string, rows: PreviewRow[]) => {
  const values = [header, ...rows.map((row) => String(row[header] ?? ''))]
  const maxLength = values.reduce(
    (longest, value) => Math.max(longest, value.length),
    0
  )

  return Math.min(Math.max(maxLength * 8 + 32, 120), 480)
}

const noWrapStyle = {
  whiteSpace: 'nowrap' as const,
  wordBreak: 'keep-all' as const,
}

const parseCsvPreview = (csvText: string) => {
  const parsed = Papa.parse(csvText, {
    header: true,
    skipEmptyLines: true,
  })
  const parsedRows = (parsed.data ?? []) as Record<string, string>[]

  const headers = parsed.meta.fields ?? Object.keys(parsedRows[0] ?? {})
  const rows: PreviewRow[] = parsedRows.map((row, index) => {
    const previewRow: PreviewRow = {
      key: String(index),
      _rowNum: index + 1,
    }

    for (const header of headers) {
      previewRow[header] = row[header] ?? ''
    }

    return previewRow
  })

  return { headers, rows }
}

const CensusFilePreviewModal = <T extends PreviewableCensusFile>({
  open,
  file,
  getDownloadUrl,
  censusVersion,
  onFileSubmit,
  isSubmitting = false,
  onCancel,
}: GenericProps<T>) => {
  const [downloadUrl, setDownloadUrl] = useState('')
  const [headers, setHeaders] = useState<string[]>([])
  const [rows, setRows] = useState<PreviewRow[]>([])
  const [errorMessage, setErrorMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!open || !file) {
      setDownloadUrl('')
      setHeaders([])
      setRows([])
      setErrorMessage('')
      setIsLoading(false)
      return
    }

    let isCancelled = false

    const loadPreview = async () => {
      try {
        setIsLoading(true)
        setErrorMessage('')

        const { url } = await getDownloadUrl(file.id)

        if (!url) {
          throw new Error('Missing preview URL')
        }

        const response = await fetch(url)

        if (!response.ok) {
          throw new Error('Failed to fetch file content')
        }

        const csvText = await response.text()
        const { headers: nextHeaders, rows: nextRows } =
          parseCsvPreview(csvText)

        if (isCancelled) {
          return
        }

        setDownloadUrl(url)
        setHeaders(nextHeaders)
        setRows(nextRows)

        if (nextHeaders.length === 0) {
          setErrorMessage('This file does not contain previewable rows.')
        }
      } catch (error) {
        if (isCancelled) {
          return
        }

        console.error('Failed to preview census file', error)
        setErrorMessage(
          'Unable to preview this file right now. You can still open the file in a new tab.'
        )
      } finally {
        if (!isCancelled) {
          setIsLoading(false)
        }
      }
    }

    void loadPreview()

    return () => {
      isCancelled = true
    }
  }, [file, getDownloadUrl, open])

  const columns = useMemo(
    () => [
      {
        title: '#',
        dataIndex: '_rowNum',
        key: '_rowNum',
        width: 56,
        onHeaderCell: () => ({
          style: noWrapStyle,
        }),
        onCell: () => ({
          style: noWrapStyle,
        }),
      },
      ...headers.map((header) => ({
        title: header,
        dataIndex: header,
        key: header,
        width: getColumnWidth(header, rows),
        onHeaderCell: () => ({
          style: noWrapStyle,
        }),
        onCell: () => ({
          style: noWrapStyle,
        }),
      })),
    ],
    [headers, rows]
  )

  const downloadFile = (url: string, fileName: string) => {
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    link.rel = 'noopener noreferrer'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleDownloadFile = async () => {
    if (!file) {
      return
    }

    if (downloadUrl) {
      downloadFile(downloadUrl, file.fileName)
      return
    }

    try {
      const { url } = await getDownloadUrl(file.id)
      if (!url) {
        throw new Error('Missing preview URL')
      }

      setDownloadUrl(url)
      downloadFile(url, file.fileName)
    } catch (error) {
      console.error('Failed to download census file', error)
    }
  }

  const canSubmit =
    file !== null &&
    onFileSubmit !== undefined &&
    censusVersion !== undefined &&
    !file.isSubmitted &&
    (censusVersion === 2 || !file.isError)

  const handleSubmit = () => {
    if (!canSubmit || !file || censusVersion === undefined || !onFileSubmit) {
      return
    }

    onFileSubmit(file, censusVersion)
  }

  return (
    <Modal
      title={file ? `Preview: ${file.fileName}` : 'Preview'}
      open={open}
      onCancel={onCancel}
      width={1000}
      destroyOnClose
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {canSubmit ? (
            <Button
              key="submit"
              variant="darkblue"
              onClick={handleSubmit}
              loading={isSubmitting}
            >
              Submit
            </Button>
          ) : null}

          <Button
            key="download-file"
            variant="secondary"
            type="outline"
            onClick={handleDownloadFile}
            disabled={!file}
            style={{ marginLeft: 8 }}
          >
            Download file
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Text type="secondary">Showing all rows in this file.</Text>

        {errorMessage ? (
          <Alert type="warning" showIcon message={errorMessage} />
        ) : null}

        {isLoading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 240,
            }}
          >
            <Spin />
          </div>
        ) : (
          <Table
            dataSource={rows}
            columns={columns as any}
            pagination={false}
            size="small"
            scroll={{ x: 'max-content', y: 480 }}
            locale={{ emptyText: 'No preview data available' }}
          />
        )}
      </div>
    </Modal>
  )
}

export default CensusFilePreviewModal
